import React, { createContext, useContext, useMemo, useRef } from 'react'

import { identity } from 'es-toolkit'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import {
  buildResolvedWikiSourceFiles,
  buildWikiSourceFileNameById,
} from '@/components/pursuits/wiki/ui/sourceFiles'
import type {
  ResolvedWikiSourceFile,
  WikiSourceFileNameById,
} from '@/components/pursuits/wiki/ui/sourceFiles'
import type {
  WikiCitationStore,
  WikiCitationStoreState,
} from '@/components/pursuits/wiki/ui/store'
import { createWikiCitationStore } from '@/components/pursuits/wiki/ui/store'

import type { Source } from '@/types/chat-v2'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

interface WikiCitationDataContextValue {
  orgId: UUID | ''
  pursuitId: UUID | ''
  sources: Record<number, Source>
  sourceList: Source[]
  sourceFileIds: UUID[]
  filesById: Map<UUID, File>
  sourceFileNameById: WikiSourceFileNameById
  deletedSourceFileIds: UUID[]
  deletedSourceFileIdSet: Set<UUID>
  resolvedSourceFiles: ResolvedWikiSourceFile[]
  pursuitFilesLoaded: boolean
  pursuitFilesError: boolean
  commentCount?: number
}

interface WikiCitationProviderProps {
  orgId: UUID | ''
  pursuitId: UUID | ''
  sources: Record<number, Source>
  sourceFileIds: UUID[]
  pursuitFiles: File[]
  pursuitFilesLoaded: boolean
  pursuitFilesError: boolean
  deletedSourceFileIds: UUID[]
  commentCount?: number
  children: React.ReactNode
}

export const WikiCitationContext = createContext<WikiCitationStore | undefined>(
  undefined,
)

const WikiCitationDataContext = createContext<
  WikiCitationDataContextValue | undefined
>(undefined)

export const WikiCitationProvider: React.FC<WikiCitationProviderProps> = ({
  orgId,
  pursuitId,
  sources,
  sourceFileIds,
  pursuitFiles,
  pursuitFilesLoaded,
  pursuitFilesError,
  deletedSourceFileIds,
  commentCount,
  children,
}) => {
  const storeRef = useRef<WikiCitationStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createWikiCitationStore()
  }

  const derivedData = useMemo(() => {
    const sourceList = Object.values(sources)
    const filesById = new Map(pursuitFiles.map((file) => [file.id, file]))
    const deletedSourceFileIdSet = new Set(deletedSourceFileIds)
    const sourceFileNameById = buildWikiSourceFileNameById({
      sources: sourceList,
      loadedFiles: pursuitFiles,
    })
    return {
      sourceList,
      filesById,
      sourceFileNameById,
      deletedSourceFileIdSet,
      resolvedSourceFiles: buildResolvedWikiSourceFiles({
        sourceFileIds,
        filesById,
        sourceFileNameById,
        deletedSourceFileIds: deletedSourceFileIdSet,
      }),
    }
  }, [sources, sourceFileIds, pursuitFiles, deletedSourceFileIds])

  const dataValue = useMemo<WikiCitationDataContextValue>(
    () => ({
      orgId,
      pursuitId,
      sources,
      sourceFileIds,
      deletedSourceFileIds,
      pursuitFilesLoaded,
      pursuitFilesError,
      commentCount,
      ...derivedData,
    }),
    [
      orgId,
      pursuitId,
      sources,
      sourceFileIds,
      deletedSourceFileIds,
      pursuitFilesLoaded,
      pursuitFilesError,
      commentCount,
      derivedData,
    ],
  )

  return (
    <WikiCitationContext.Provider value={storeRef.current}>
      <WikiCitationDataContext.Provider value={dataValue}>
        {children}
      </WikiCitationDataContext.Provider>
    </WikiCitationContext.Provider>
  )
}

export const useWikiCitationContext = () => {
  const context = useContext(WikiCitationContext)
  if (!context) {
    throw new Error(
      'useWikiCitationContext must be used within a WikiCitationProvider',
    )
  }
  return context
}

const useWikiCitationDataContext = () => {
  const context = useContext(WikiCitationDataContext)
  if (!context) {
    throw new Error(
      'useWikiCitationDataContext must be used within a WikiCitationProvider',
    )
  }
  return context
}

export const useWikiCitationStore = <T,>(
  selector: (state: WikiCitationStoreState) => T = identity as (
    state: WikiCitationStoreState,
  ) => T,
) => useStore(useWikiCitationContext(), selector)

export const useWikiCitationOrgId = () => useWikiCitationDataContext().orgId

export const useWikiCitationPursuitId = () =>
  useWikiCitationDataContext().pursuitId

export const useWikiCitationSources = () => useWikiCitationDataContext().sources

export const useWikiCitationSourcesCitationList = () =>
  useWikiCitationDataContext().sourceList

export const useWikiCitationSource = (sourceId: number | null) => {
  const { sources } = useWikiCitationDataContext()
  return sourceId !== null ? (sources[sourceId] ?? null) : null
}

export const useWikiDeletedSourceFileIds = () =>
  useWikiCitationDataContext().deletedSourceFileIds

export const useWikiDeletedSourceFileIdSet = () =>
  useWikiCitationDataContext().deletedSourceFileIdSet

export const useWikiCitationFilesById = () =>
  useWikiCitationDataContext().filesById

export const useWikiCitationSourceFileNameById = () =>
  useWikiCitationDataContext().sourceFileNameById

export const useWikiResolvedSourceFiles = () =>
  useWikiCitationDataContext().resolvedSourceFiles

export const useWikiCitationPursuitFilesLoaded = () =>
  useWikiCitationDataContext().pursuitFilesLoaded

export const useWikiCitationPursuitFilesError = () =>
  useWikiCitationDataContext().pursuitFilesError

export const useWikiCitationCommentCount = () =>
  useWikiCitationDataContext().commentCount

export const useWikiCitationSourceFileIds = () =>
  useWikiCitationDataContext().sourceFileIds

export const useWikiTocOpen = () =>
  useWikiCitationStore((state) => state.tocOpen)

export const useWikiSetTocOpen = () =>
  useWikiCitationStore((state) => state.setTocOpen)

export const useWikiSourceViewerState = () =>
  useWikiCitationStore((state) => state.sourceViewerState)

export const useWikiSetSourceViewerState = () =>
  useWikiCitationStore((state) => state.setSourceViewerState)

export const useWikiOpenSourceViewer = () =>
  useWikiCitationStore((state) => state.openSourceViewer)

export const useWikiCloseSourceViewer = () =>
  useWikiCitationStore((state) => state.closeSourceViewer)

export const useWikiToggleDiscussion = () =>
  useWikiCitationStore((state) => state.toggleDiscussion)

export const useWikiCitationActiveSourceIds = () =>
  useWikiCitationStore(useShallow((state) => state.activeSourceIds))

export const useWikiCitationSetActiveSourceIds = () =>
  useWikiCitationStore((state) => state.setActiveSourceIds)

export const useWikiCitationActiveSources = () => {
  const activeSourceIds = useWikiCitationActiveSourceIds()
  const sources = useWikiCitationSources()

  return useMemo(
    () =>
      activeSourceIds
        .map((id) => sources[id])
        .filter((source): source is Source => source != null),
    [activeSourceIds, sources],
  )
}

export const useWikiCitationActiveSource = () => {
  const activeSourceIds = useWikiCitationActiveSourceIds()
  const sources = useWikiCitationSources()
  const firstActiveSourceId = activeSourceIds[0]

  return firstActiveSourceId != null
    ? (sources[firstActiveSourceId] ?? null)
    : null
}

export const useWikiCitationSidebarOpen = () =>
  useWikiCitationStore((state) => state.sourceViewerState !== 'hidden')

export const useWikiCitationActiveTab = () =>
  useWikiCitationStore((state) => state.activeTab)

export const useWikiCitationSetActiveTab = () =>
  useWikiCitationStore((state) => state.setActiveTab)

export const useWikiCitationViewingFileId = () =>
  useWikiCitationStore((state) => state.viewingFileId)

export const useWikiCitationSetViewingFileId = () =>
  useWikiCitationStore((state) => state.setViewingFileId)
